import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './marketerteam.scss';
import { ReactComponent as TeamLogo } from '../../images/teamLogo.svg';
import { ReactComponent as Back } from '../../images/back.svg';
import Button from '../../components/Button/Button';
import { ReactComponent as Avatar } from '../../images/avatarDefault.svg';
import Modal from '../../components/Modal/Modal';
import { ReactComponent as CloseIcon } from '../../images/Close.svg';
import Input from '../../components/Input/Input';
import AgentSelection from '../../components/AgentSelection/AgentSelection';
import base from '../../apis';
import { useSelector } from 'react-redux';
import Spinner from '../../components/Spinner/Spinner';
import { toast } from 'react-toastify';
import { MdModeEditOutline } from 'react-icons/md';

export default function MarketerTeam() {
  const { teamId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isFromSenior = pathname.includes('assign-team-to-snr-marketer');
  const isManage = pathname.includes('mart-exec-teams');
  const [members, setMembers] = useState([]);
  const [addNewModalOpen, setAddNewModalOpen] = useState(false);
  const [addSnrOpen, setAddSnrOpen] = useState(false);
  const [snrMkter, setSnrMkter] = useState(null);
  const [marketersToAdd, setMarketersToAdd] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const MARKETERS = useRef([]);
  const [exec_data, setExecData] = useState([]);
  const ORIGINALS = useRef([]);
  const [snrMkts, setSnrMkts] = useState({ isLoading: false, data: [] });
  const SNR_MARKETERS = useRef([]);
  const [snrToBe, setSnrToBe] = useState('');
  const NAME = useRef('');

  const {
    state: { color, name, members_count },
  } = useLocation();
  const execs = useSelector((state) => state.marketingExec.marketingExecutives);

  useEffect(() => {
    if (execs.length) {
      setExecData(execs);
      MARKETERS.current = execs;
    }
  }, [execs]);

  const getTeamDetails = React.useCallback(
    (id = teamId) => {
      const url = `/operations/marketing_teams/${id}/`;
      setMembers([]);
      setSnrMkter(null);
      setIsLoading(true);
      base
        .get(url)
        .then(({ data }) => {
          setIsLoading(false);
          if (data.snr_marketer) {
            setSnrMkter(data.snr_marketer);
          } else {
            toast.warn('No Senior Marketer assigned to this team yet');
          }
          if (!data.members.length) {
            toast.warn('No members in this team yet');
          }
          setTeamName(data.name);
          NAME.current = data.name;
          setMembers(data.members);
          const originals = data.members.map((item) => item.id);
          setMarketersToAdd(originals);
          ORIGINALS.current = originals;
        })
        .catch((err) => {
          toast.error('Error getting team details');
          setIsLoading(false);
        });
    },
    [teamId]
  );

  const toggleAgentSelection = (id) => {
    if (marketersToAdd.includes(id)) {
      setMarketersToAdd(marketersToAdd.filter((agent) => agent !== id));
    } else {
      setMarketersToAdd([...marketersToAdd, id]);
    }
  };

  React.useEffect(() => {
    getTeamDetails(teamId);
  }, [teamId, getTeamDetails]);

  const closeModal = () => {
    setAddNewModalOpen(false);
    setMarketersToAdd(ORIGINALS.current);
    setExecData(MARKETERS.current);
    setTeamName(NAME.current);
  };

  const onSearch = (e) => {
    const value = e.target.value;
    // setSearchParam(value);
    const filtered = MARKETERS.current.filter((item) => {
      const name = item.user.first_name + item.user.last_name;
      return name.toLowerCase().includes(value.toLowerCase());
    });
    setExecData(filtered);
  };

  const onUpdateTeam = () => {
    setIsLoading(true);
    const body = {
      name: teamName,
      members: marketersToAdd.map((id) => ({ id })),
    };
    base
      .put(`/operations/marketing_teams/${teamId}/`, body)
      .then(({ data }) => {
        setAddNewModalOpen(false);
        setIsLoading(false);
        toast.success('Members added successfully');
        getTeamDetails();
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error('Error adding members');
      });
  };

  const getSnr = () => {
    setSnrMkts({ isLoading: true, data: [] });
    base
      .get('/operations/snr_marketers/')
      .then(({ data }) => {
        if (!data.length) {
          toast.warn('No senior marketers found');
        }
        SNR_MARKETERS.current = data;
        setSnrMkts({ isLoading: false, data });
      })
      .catch((err) => {
        toast.error('Error getting senior marketers');
        setSnrMkts({ isLoading: false, data: [] });
      });
  };

  const onOpenSnrMkt = (useSnr) => {
    setAddSnrOpen(true);
    setSnrToBe(useSnr ? snrMkter?.id : '');
    getSnr();
  };

  const handleSnrSearch = (e) => {
    const value = e.target.value;
    if (value === '') setSnrMkts({ isLoading: false, data: SNR_MARKETERS.current });
    const filtered = SNR_MARKETERS.current.filter((item) => {
      const name = item.user.first_name + item.user.last_name;
      return name.toLowerCase().includes(value.toLowerCase());
    });
    setSnrMkts({ isLoading: false, data: filtered });
  };

  const toggleSnr = (id) => {
    if (snrToBe === id) {
      setSnrToBe('');
    } else {
      setSnrToBe(id);
    }
  };

  const onSetSnrMkter = () => {
    setIsLoading(true);

    base
      .put(`/operations/marketing_teams/${teamId}/`, {
        snr_marketer_id: snrToBe || 0,
      })

      .then((data) => {
        //todo set snr mkt to state
        setAddSnrOpen(false);
        // setIsLoading(false);
        toast.success(`Senior Marketer assignment changed successfully`);
        setSnrToBe('');
        getTeamDetails();
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          toast.error('Senior Marketer already assigned to team');
          return;
        }
        toast.error('Error setting senior marketer');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      {isLoading && <Spinner />}
      <Modal open={addNewModalOpen} onClose={closeModal}>
        <div className="add-new-modal">
          <div className="add-new-modal__header">
            <h1 style={{ marginRight: '100px' }}>Add New Team Members</h1>
            <Button
              style={{
                width: '100px',
                fontSize: '18px',
                padding: '10px',
                color: 'white',
                backgroundColor: 'var(--primary-color)',
                display: 'block',
              }}
              onClick={onUpdateTeam}
              text="Save"
            />

            <CloseIcon className="closer" onClick={closeModal} />
          </div>
          <Input
            fullStyles={{ width: '50%', margin: '10px auto' }}
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
          />
          <p className="add-new-modal__lighter">Select Marketing Executives to add to the team</p>
          <div className="add-new-modal__content">
            <div>
              <h3>Members ({marketersToAdd.length})</h3>
              <Input
                // value={searchParam}
                type="search"
                onChange={onSearch}
                placeholder="Enter a name"
              />
            </div>
            <div className="add-new-modal__hidden">
              {exec_data &&
                exec_data.map(({ id, user: { first_name, last_name }, team_id }) => (
                  <AgentSelection
                    name={first_name + ' ' + last_name}
                    selected={marketersToAdd.includes(id)}
                    onToggle={() => toggleAgentSelection(id)}
                    key={id}
                    assigned={team_id || ORIGINALS.current.includes(id)}
                  />
                ))}
            </div>
          </div>
        </div>
      </Modal>
      <Modal open={addSnrOpen} onClose={() => setAddSnrOpen(false)}>
        <div className="add-new-modal">
          <div className="add-new-modal__header">
            <h1 style={{ marginRight: '100px' }}>{teamName}</h1>
            <Button
              style={{
                width: '100px',
                fontSize: '18px',
                padding: '10px',
                color: 'white',
                backgroundColor: 'var(--primary-color)',
                display: 'block',
              }}
              onClick={onSetSnrMkter}
              text="Save"
            />

            <CloseIcon className="closer" onClick={() => setAddSnrOpen(false)} />
          </div>
          <p className="add-new-modal__lighter">Select A Senior Marketing Executive to lead the team</p>
          {snrMkts.isLoading ? (
            <Spinner overlay={false} />
          ) : (
            <div className="add-new-modal__content">
              <div>
                <h3>Snr Marketers</h3>
                <Input placeholder="Enter a name" onChange={handleSnrSearch} type="search" />
              </div>
              <div className="add-new-modal__hidden">
                {snrMkts.data &&
                  snrMkts.data.map(({ id, user: { first_name, last_name }, team_id }) => (
                    <AgentSelection
                      name={first_name + ' ' + last_name}
                      selected={snrToBe === id}
                      onToggle={() => toggleSnr(id)}
                      key={id}
                      assigned={team_id}
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
      <div className="marketerteam">
        <div className="marketerteam__head">
          <Back cursor="pointer" onClick={() => navigate(-1)} />

          <TeamLogo color={color} transform="scale(0.5)" />
          <div>
            <h4>{teamName || name}</h4>
            <span>{members.length || members_count}</span>
          </div>
          {!isFromSenior &&
            (!isManage ? (
              <Button
                type="submit"
                style={{
                  width: 'fit-content',
                  fontSize: '16px',
                  padding: '10px 20px',
                  color: 'white',
                  backgroundColor: 'var(--primary-color)',
                  display: 'block',
                  marginLeft: 'auto',
                  marginTop: '0',
                }}
                onClick={() => setAddNewModalOpen(true)}
                text="Manage Members"
              />
            ) : (
              <Button
                style={{
                  width: 'fit-content',
                  fontSize: '16px',
                  padding: '10px 20px',
                  color: 'white',
                  backgroundColor: 'var(--primary-color)',
                  display: 'block',
                  marginLeft: 'auto',
                  marginTop: '0',
                }}
                onClick={() => navigate('/assign-mk-to-team')}
                text="Manage Teams"
              />
            ))}
        </div>
        <div>
          {
            <>
              {/* <h2 style={{ color: '#98C8DD' }}>No team members</h2> */}
              <div className="marketerteam__body">
                {
                  <AgentCard
                    name={snrMkter?.user.first_name + ' ' + snrMkter?.user.last_name}
                    email={snrMkter?.user.email}
                    snrAssigned={snrMkter?.user}
                    isSnr={true}
                    fromSenior={isFromSenior}
                    onClick={onOpenSnrMkt}
                  />
                }
                {/* <AgentCard isSnr={true} onClick={() => setAddSnrOpen(true)} /> */}
                {members.map((member, index) => (
                  <AgentCard
                    name={member.user.first_name + ' ' + member.user.last_name}
                    email={member.user.email}
                    key={member.id}
                  />
                ))}
              </div>
            </>
          }
        </div>
      </div>
    </>
  );
}

const AgentCard = ({ name = 'test', email = 'fsdf', isSnr, snrAssigned, onClick, fromSenior = false }) => {
  const { pathname } = useLocation();

  const isManage = pathname.includes('mart-exec-teams');

  return !isSnr ? (
    <div className="marketerteam__agentcard">
      <Avatar style={{ marginTop: '20px' }} />
      <h4 style={{ marginTop: 'auto' }}>{name || 'Full Name'}</h4>
      <span style={{ color: 'var(--grey)', fontSize: '14px' }}>{email || 'Email'}</span>
    </div>
  ) : (
    <div className="marketerteam__agentcard" style={{ position: 'relative' }}>
      <h4>Snr Mkt. Executive</h4>
      {snrAssigned && fromSenior && (
        <MdModeEditOutline size={20} className="marketerteam__agentcard-edit" onClick={() => onClick(true)} />
      )}
      <Avatar />
      {snrAssigned ? (
        <>
          <h4>{name || 'Full Name'}</h4>
          <span>{email || 'Email'}</span>
        </>
      ) : (
        <>
          <span>None Yet</span>
          {!isManage && (
            <Button
              style={{
                fontSize: '18px',
                padding: '10px',
                color: 'white',
                backgroundColor: 'var(--primary-color)',
                display: 'block',
              }}
              onClick={onClick}
              text="ADD"
            />
          )}
        </>
      )}
    </div>
  );
};
